import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectAccountData } from '../../redux/main/accountSlice';
import { selectUserData } from '../../redux/main/userSlice';
import { useColorPalette } from '../../hooks/useColorPalette';
import { getApiBaseUrl } from '../../config/api.config';
import { toast } from 'react-toastify';
import {
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    Form,
    FormGroup,
    Label,
    Input,
    Badge,
    ListGroup,
    ListGroupItem,
    Spinner
} from 'reactstrap';
import EmployeeSelect from '../EmployeeSelect';
import DynamicIcon from '../DynamicIcon';

export default function UserGroupsModal({ isOpen, toggle, onGroupsChanged }) {
    const palette = useColorPalette();
    const accountData = useSelector(selectAccountData);
    const userData = useSelector(selectUserData);
    const token = localStorage.getItem('installamelo-auth-token');

    // Stati
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(null);
    const [editMode, setEditMode] = useState(null); // 'create', 'edit', 'view'
    const [formData, setFormData] = useState({
        name: '',
        description: ''
    });
    const [selectedMembers, setSelectedMembers] = useState([]);
    const [saving, setSaving] = useState(false);

    // Carica gruppi
    const loadGroups = async () => {
        if (!accountData?._id) return;
        setLoading(true);
        try {
            const response = await fetch(
                `${getApiBaseUrl()}/userGroups/list?accountId=${accountData._id}`,
                { headers: { 'auth-token': token } }
            );
            if (response.ok) {
                const data = await response.json();
                setGroups(data);
            }
        } catch (error) {
            console.error('Errore caricamento gruppi:', error);
            toast.error('Errore nel caricamento dei gruppi');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            loadGroups();
            setEditMode(null);
            setSelectedGroup(null);
        }
    }, [isOpen, accountData?._id]);

    // Apri form per nuovo gruppo
    const handleCreateNew = () => {
        setEditMode('create');
        setSelectedGroup(null);
        setFormData({ name: '', description: '' });
        setSelectedMembers([]);
    };

    // Apri form per modifica gruppo
    const handleEditGroup = (group) => {
        setEditMode('edit');
        setSelectedGroup(group);
        setFormData({
            name: group.name,
            description: group.description || ''
        });
        // Converti membri nel formato per EmployeeSelect
        setSelectedMembers((group.members || []).map(m => ({
            value: m.userId,
            label: m.userLabel,
            data: { _id: m.userId }
        })));
    };

    // Torna alla lista
    const handleBackToList = () => {
        setEditMode(null);
        setSelectedGroup(null);
    };

    // Salva gruppo
    const handleSave = async () => {
        if (!formData.name.trim()) {
            toast.error('Il nome del gruppo è obbligatorio');
            return;
        }

        setSaving(true);
        try {
            const members = selectedMembers.map(m => ({
                userId: m.value,
                userLabel: m.label,
                addedAt: new Date()
            }));

            const payload = {
                accountId: accountData._id,
                name: formData.name.trim(),
                description: formData.description.trim(),
                members,
                createdBy: userData?._id,
                createdByLabel: `${userData?.name} ${userData?.surname}`
            };

            let response;
            if (editMode === 'create') {
                response = await fetch(`${getApiBaseUrl()}/userGroups`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': token
                    },
                    body: JSON.stringify(payload)
                });
            } else {
                response = await fetch(`${getApiBaseUrl()}/userGroups/${selectedGroup._id}`, {
                    method: 'PATCH',
                    headers: {
                        'Content-Type': 'application/json',
                        'auth-token': token
                    },
                    body: JSON.stringify(payload)
                });
            }

            if (response.ok) {
                toast.success(editMode === 'create' ? 'Gruppo creato con successo' : 'Gruppo aggiornato');
                loadGroups();
                handleBackToList();
                if (onGroupsChanged) onGroupsChanged();
            } else {
                const error = await response.json();
                toast.error(error.message || 'Errore nel salvataggio');
            }
        } catch (error) {
            console.error('Errore salvataggio gruppo:', error);
            toast.error('Errore nel salvataggio del gruppo');
        } finally {
            setSaving(false);
        }
    };

    // Elimina gruppo
    const handleDelete = async (group) => {
        if (!window.confirm(`Sei sicuro di voler eliminare il gruppo "${group.name}"?`)) {
            return;
        }

        try {
            const response = await fetch(`${getApiBaseUrl()}/userGroups/${group._id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({ accountId: accountData._id })
            });

            if (response.ok) {
                toast.success('Gruppo eliminato');
                loadGroups();
                if (onGroupsChanged) onGroupsChanged();
            } else {
                toast.error('Errore nell\'eliminazione del gruppo');
            }
        } catch (error) {
            console.error('Errore eliminazione gruppo:', error);
            toast.error('Errore nell\'eliminazione del gruppo');
        }
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle} size="lg">
            <ModalHeader toggle={toggle}>
                <DynamicIcon icon="fi-rr-users" className="me-2" />
                {editMode === 'create' ? 'Nuovo Gruppo' : editMode === 'edit' ? 'Modifica Gruppo' : 'Gestione Gruppi Utenti'}
            </ModalHeader>
            <ModalBody>
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" />
                        <p className="mt-2">Caricamento gruppi...</p>
                    </div>
                ) : editMode ? (
                    // Form creazione/modifica
                    <Form>
                        <FormGroup>
                            <Label for="groupName">Nome Gruppo *</Label>
                            <Input
                                type="text"
                                id="groupName"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Es: Tecnici Senior, Team Manutenzione..."
                                maxLength={50}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label for="groupDescription">Descrizione</Label>
                            <Input
                                type="textarea"
                                id="groupDescription"
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Descrizione opzionale del gruppo"
                                rows={2}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Membri del Gruppo</Label>
                            <EmployeeSelect
                                selectedOptions={selectedMembers}
                                setSelectedOptions={setSelectedMembers}
                                isMulti={true}
                                placeholder="Seleziona i membri del gruppo..."
                            />
                            <small className="text-muted">
                                {selectedMembers.length} {selectedMembers.length === 1 ? 'membro' : 'membri'} selezionati
                            </small>
                        </FormGroup>
                    </Form>
                ) : (
                    // Lista gruppi
                    <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <span className="text-muted">{groups.length} gruppi</span>
                            <Button
                                color="primary"
                                size="sm"
                                onClick={handleCreateNew}
                                style={{ backgroundColor: palette.primary, borderColor: palette.primary }}
                            >
                                <i className="fi fi-rr-plus me-1"></i>
                                Nuovo Gruppo
                            </Button>
                        </div>

                        {groups.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                                <DynamicIcon icon="fi-rr-users" style={{ fontSize: 48, opacity: 0.3 }} />
                                <p className="mt-2">Nessun gruppo creato</p>
                                <Button color="link" onClick={handleCreateNew}>
                                    Crea il primo gruppo
                                </Button>
                            </div>
                        ) : (
                            <ListGroup flush>
                                {groups.map(group => (
                                    <ListGroupItem 
                                        key={group._id}
                                        className="d-flex justify-content-between align-items-center py-3"
                                    >
                                        <div>
                                            <div className="d-flex align-items-center">
                                                <i className="fa-solid fa-users me-2" style={{ color: '#666' }}></i>
                                                <strong>{group.name}</strong>
                                                <Badge color="secondary" className="ms-2">
                                                    {group.members?.length || 0} membri
                                                </Badge>
                                            </div>
                                            {group.description && (
                                                <small className="text-muted d-block mt-1">
                                                    {group.description}
                                                </small>
                                            )}
                                        </div>
                                        <div className="d-flex gap-1">
                                            <Button
                                                color="link"
                                                size="sm"
                                                onClick={() => handleEditGroup(group)}
                                                title="Modifica"
                                                style={{ color: '#333' }}
                                            >
                                                <i className="fa-solid fa-pencil"></i>
                                            </Button>
                                            <Button
                                                color="link"
                                                size="sm"
                                                onClick={() => handleDelete(group)}
                                                title="Elimina"
                                                style={{ color: '#dc3545' }}
                                            >
                                                <i className="fa-solid fa-trash"></i>
                                            </Button>
                                        </div>
                                    </ListGroupItem>
                                ))}
                            </ListGroup>
                        )}
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                {editMode ? (
                    <>
                        <Button color="secondary" onClick={handleBackToList}>
                            <i className="fi fi-rr-arrow-left me-1"></i>
                            Indietro
                        </Button>
                        <Button
                            color="primary"
                            onClick={handleSave}
                            disabled={saving}
                            style={{ backgroundColor: palette.primary, borderColor: palette.primary }}
                        >
                            {saving ? <Spinner size="sm" className="me-1" /> : <i className="fi fi-rr-check me-1"></i>}
                            {editMode === 'create' ? 'Crea Gruppo' : 'Salva Modifiche'}
                        </Button>
                    </>
                ) : (
                    <Button color="secondary" onClick={toggle}>
                        Chiudi
                    </Button>
                )}
            </ModalFooter>
        </Modal>
    );
}
